import { useId } from 'preact/hooks';
import { cx } from './internal';

interface VChartProps {
  /** Numeric series. Empty array renders a graceful "no data" state. */
  data: number[];
  /** Chart shape. Default `'line'`. */
  variant?: 'line' | 'bar' | 'area';
  /** Stroke / fill color. Defaults to the design-system primary token. */
  color?: string;
  /** Pixel height of the chart. Default 160. */
  height?: number;
  /** Optional caption above the chart. */
  label?: string;
  className?: string;
  /**
   * X-axis tick labels (parallel to `data`). Long sets are thinned to
   * first / middle / last so ticks never collide at phone widths.
   */
  ticks?: string[];
  /**
   * Show a quiet dot at the last data point. Default true for line/area.
   */
  livePulse?: boolean;
  /** Show horizontal reference hairlines + y extent labels. Default true. */
  showGrid?: boolean;
}

const DEFAULT_HEIGHT = 160;
const VIEWBOX_WIDTH = 320;
const PADDING_X = 6;
const PADDING_Y = 10;

// §design-reset — reference-grade chart, not "breathtaking-by-default".
//   • SVG presentation attributes are written KEBAB-CASE ('stroke-width').
//     CamelCase props fall through preact's setAttribute un-lowercased on the
//     SVG namespace, so 'strokeOpacity' silently never applied — the grid
//     rendered as full-opacity dark bars (the wireframe look).
//   • No glow filter on the stroke; depth comes from the soft area fade.
//   • Real reference data: y extent labels (min/max) + x tick labels.
//   • Hairline grid at 7% ink, solid (dashes read as a draft).
export function VChart({
  data,
  variant = 'line',
  color,
  height = DEFAULT_HEIGHT,
  label,
  className,
  ticks,
  livePulse = true,
  showGrid = true
}: VChartProps) {
  const stroke = color ?? 'var(--color-primary)';
  const vbHeight = DEFAULT_HEIGHT;
  // useId() gives us a unique gradient id even when multiple VCharts sit on
  // the same page (a shared id leaks the first gradient into every chart).
  const uid = useId().replace(/[^a-z0-9]/gi, '');
  const gradId = `vc-grad-${uid}`;

  if (!data || data.length === 0) {
    return (
      <div
        className={cx(
          'flex w-full items-center justify-center rounded-2xl border border-dashed border-black/[0.08] text-sm dark:border-white/[0.1]',
          className
        )}
        style={{ height, background: 'color-mix(in srgb, var(--color-fg, #000) 4%, transparent)', color: 'var(--color-fg-muted, #9ca3af)' }}
        role="img"
        aria-label={label ? `${label}: no data` : 'No data'}
      >
        No data
      </div>
    );
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const innerW = VIEWBOX_WIDTH - PADDING_X * 2;
  const innerH = vbHeight - PADDING_Y * 2;

  // Single-point data is degenerate — render as a horizontal line so
  // the chart isn't an empty rectangle.
  const points = data.map((v, i) => {
    const x = data.length === 1 ? VIEWBOX_WIDTH / 2 : PADDING_X + (i / (data.length - 1)) * innerW;
    const y = PADDING_Y + (1 - (v - min) / range) * innerH;
    return { x, y };
  });

  const lastPoint = points[points.length - 1]!;

  // Thin the tick labels so they never collide on a phone: ≤6 ticks render
  // all, otherwise first / middle / last.
  const tickSet = ticks && ticks.length === data.length && data.length > 1
    ? (ticks.length <= 6
        ? ticks.map((t, i) => ({ t, i }))
        : [0, Math.floor((ticks.length - 1) / 2), ticks.length - 1].map((i) => ({ t: ticks[i]!, i })))
    : null;

  const fmt = (n: number): string => {
    const a = Math.abs(n);
    if (a >= 1e6) return `${+(n / 1e6).toFixed(1)}M`;
    if (a >= 1e3) return `${+(n / 1e3).toFixed(1)}k`;
    return String(+n.toFixed(1));
  };

  return (
    <figure className={cx('flex flex-col gap-2 min-w-0', className)}>
      {label ? (
        <figcaption className="text-xs font-medium uppercase tracking-wide text-[var(--color-fg-muted)] truncate">
          {label}
        </figcaption>
      ) : null}
      <div className="relative w-full" style={{ height: height + (tickSet ? 18 : 0) }}>
        <svg
          role="img"
          aria-label={label ?? `Chart with ${data.length} points`}
          viewBox={`0 0 ${VIEWBOX_WIDTH} ${vbHeight}`}
          className="w-full"
          style={{ height, overflow: 'visible', display: 'block' }}
          preserveAspectRatio="none"
        >
          <defs>
            {/* Soft area fade — primary-tinted top to transparent. stop-color
                must live in `style` (the attribute form doesn't resolve CSS
                vars in several engines). */}
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" style={{ stopColor: stroke, stopOpacity: 0.26 }} />
              <stop offset="60%" style={{ stopColor: stroke, stopOpacity: 0.1 }} />
              <stop offset="100%" style={{ stopColor: stroke, stopOpacity: 0 }} />
            </linearGradient>
          </defs>

          {/* Hairline reference grid at 0/50/100% of the range. */}
          {showGrid && variant !== 'bar'
            ? [0, 0.5, 1].map((t) => (
                <line
                  key={t}
                  x1={PADDING_X}
                  x2={VIEWBOX_WIDTH - PADDING_X}
                  y1={PADDING_Y + t * innerH}
                  y2={PADDING_Y + t * innerH}
                  stroke="var(--color-fg, #000)"
                  stroke-width="1"
                  opacity="0.07"
                  vector-effect="non-scaling-stroke"
                />
              ))
            : null}

          {variant === 'bar' ? (
            <BarChart points={points} stroke={stroke} viewboxHeight={vbHeight} />
          ) : (
            <LineOrArea
              points={points}
              stroke={stroke}
              gradId={gradId}
              viewboxHeight={vbHeight}
              fill={variant === 'area'}
            />
          )}

          {/* Quiet endpoint dot — the "latest value" anchor. */}
          {livePulse && variant !== 'bar' && points.length > 1 ? (
            <circle
              cx={lastPoint.x}
              cy={lastPoint.y}
              r="3"
              fill={stroke}
              stroke="var(--color-bg, #fff)"
              stroke-width="1.5"
              vector-effect="non-scaling-stroke"
            />
          ) : null}
        </svg>
        {/* Y extent labels — HTML overlay so type renders crisp (SVG text under
            preserveAspectRatio:none would stretch). */}
        {/* The plot itself is inherently LTR (time flows left→right even in
            RTL apps — the platform convention), so the overlays use PHYSICAL
            left/right to stay glued to the SVG geometry. dir=ltr pins the
            digits' bidi run. */}
        {showGrid && variant !== 'bar' && max !== min ? (
          <div aria-hidden="true" dir="ltr">
            <span className="absolute tabular-nums" style={{ top: 0, right: 0, transform: 'translateY(-55%)', fontSize: '10px', fontWeight: 500, color: 'color-mix(in srgb, var(--color-fg-muted, #888) 80%, transparent)' }}>{fmt(max)}</span>
            <span className="absolute tabular-nums" style={{ top: height - PADDING_Y * (height / vbHeight), right: 0, transform: 'translateY(-45%)', fontSize: '10px', fontWeight: 500, color: 'color-mix(in srgb, var(--color-fg-muted, #888) 80%, transparent)' }}>{fmt(min)}</span>
          </div>
        ) : null}
        {/* X tick labels. */}
        {tickSet ? (
          <div className="absolute flex w-full" style={{ bottom: 0, left: 0 }} dir="ltr" aria-hidden="true">
            {tickSet.map(({ t, i }) => (
              <span
                key={i}
                className="truncate"
                style={{
                  position: 'absolute',
                  left: `${(points[i]!.x / VIEWBOX_WIDTH) * 100}%`,
                  transform: i === 0 ? 'none' : i === data.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                  fontSize: '10px',
                  fontWeight: 500,
                  maxWidth: '6rem',
                  color: 'color-mix(in srgb, var(--color-fg-muted, #888) 80%, transparent)'
                }}
              >
                {t}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </figure>
  );
}

function LineOrArea({
  points,
  stroke,
  gradId,
  viewboxHeight,
  fill
}: {
  points: { x: number; y: number }[];
  stroke: string;
  gradId: string;
  viewboxHeight: number;
  fill: boolean;
}) {
  if (points.length === 1) {
    const p = points[0]!;
    return (
      <line
        x1={PADDING_X}
        x2={VIEWBOX_WIDTH - PADDING_X}
        y1={p.y}
        y2={p.y}
        stroke={stroke}
        stroke-width="2.5"
        stroke-linecap="round"
        vector-effect="non-scaling-stroke"
      />
    );
  }

  // Catmull-Rom → cubic Bezier conversion for a smooth line. Tension
  // factor 0.5 (Cardinal-equivalent) — soft curve without overshoot.
  const path = smoothPath(points);

  return (
    <>
      {fill ? (
        <path
          d={`${path} L${points[points.length - 1]!.x},${viewboxHeight - PADDING_Y / 2} L${points[0]!.x},${viewboxHeight - PADDING_Y / 2} Z`}
          fill={`url(#${gradId})`}
        />
      ) : null}
      <path
        d={path}
        fill="none"
        stroke={stroke}
        stroke-width="2.25"
        stroke-linecap="round"
        stroke-linejoin="round"
        vector-effect="non-scaling-stroke"
      />
    </>
  );
}

function BarChart({
  points,
  stroke,
  viewboxHeight
}: {
  points: { x: number; y: number }[];
  stroke: string;
  viewboxHeight: number;
}) {
  // Compute bar width from spacing between adjacent points.
  const spacing = points.length > 1 ? points[1]!.x - points[0]!.x : VIEWBOX_WIDTH * 0.6;
  const barW = Math.max(4, spacing * 0.62);
  return (
    <g>
      {points.map((p, i) => {
        const x = p.x - barW / 2;
        const y = p.y;
        const h = Math.max(2, viewboxHeight - PADDING_Y - y);
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barW}
            height={h}
            rx={3}
            fill={stroke}
            fill-opacity="0.9"
            style={{
              animation: `vc-bar-in 600ms cubic-bezier(0.34, 1.34, 0.64, 1) ${i * 40}ms both`,
              transformOrigin: `${p.x}px ${viewboxHeight}px`
            }}
          />
        );
      })}
      <style>{`
        @keyframes vc-bar-in {
          from { transform: scaleY(0); }
          to   { transform: scaleY(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          rect { animation: none !important; }
        }
      `}</style>
    </g>
  );
}

function smoothPath(pts: { x: number; y: number }[]): string {
  // Standard Catmull-Rom-to-Bezier: each segment uses neighbors as
  // control-point references for a continuous curve.
  if (pts.length < 2) return '';
  if (pts.length === 2) return `M${pts[0]!.x},${pts[0]!.y} L${pts[1]!.x},${pts[1]!.y}`;

  let d = `M${pts[0]!.x},${pts[0]!.y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[i + 2] ?? p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`;
  }
  return d;
}
